import { Cache } from '@urql/exchange-graphcache';

import {
  GQDeleteOrgCompanyMutation,
  GQPostMeasurementSupplierMappingTableRowsQuery,
  GQPreMeasurementSupplierMappingTableRowsQuery,
  GQUpsertOrgCompanyForNameMutation,
} from '../../generated/graphql-operations';
import {
  PostMeasurementSupplierMappingTableRowsDocument,
  PreMeasurementSupplierMappingTableRowsDocument,
} from '../../generated/urql';

export enum CachedSupplierMappingTableRowAction {
  DeleteOrgCompany = 'DeleteOrgCompany',
  UpdateOrgCompany = 'UpdateOrgCompany',
}

type SimplifiedCachedSupplierMappingTableRow = {
  node: {
    rawVendorEntity: string;
    orgCompany: {
      id: string;
    } | null;
    orgCompanyHasSupplierSpecificEF: boolean | null;
    autoMatched: {
      confidenceScore: number;
    } | null;
  } | null;
} | null;

export function deleteOrgCompanyInCachedSupplierMappingTableRow(
  rows: Array<SimplifiedCachedSupplierMappingTableRow>,
  result: GQDeleteOrgCompanyMutation
): void {
  const idx = rows.findIndex(
    (row) => row?.node?.orgCompany?.id === result.deleteOrgCompany.id
  );
  if (idx !== -1) {
    const node = rows[idx]?.node;
    if (node) {
      node.orgCompany = null;
      node.orgCompanyHasSupplierSpecificEF = false;
      node.autoMatched = null;
    }
  }
}

export function updateOrgCompanyInCachedSupplierMappingTableRow(
  rows: Array<SimplifiedCachedSupplierMappingTableRow>,
  result: GQUpsertOrgCompanyForNameMutation
) {
  const idx = rows.findIndex(
    (row) =>
      row?.node?.rawVendorEntity ===
      result.upsertOrgCompanyForName.orgCompany.name
  );
  if (idx !== -1) {
    const node = rows[idx]?.node;
    if (node) {
      node.orgCompany = result.upsertOrgCompanyForName.orgCompany;
      node.orgCompanyHasSupplierSpecificEF =
        result.upsertOrgCompanyForName.orgCompanyHasSupplierSpecificEF;

      // This assumes that the system user will never trigger this mutation
      // i.e. this should always result in a manual match/un-match
      node.autoMatched = null;
    }
  }
}

const mapActionToUpdateFn = {
  DeleteOrgCompany: deleteOrgCompanyInCachedSupplierMappingTableRow,
  UpdateOrgCompany: updateOrgCompanyInCachedSupplierMappingTableRow,
};

export function updateCachedSupplierMappingRows(
  cache: Cache,
  action: CachedSupplierMappingTableRowAction.DeleteOrgCompany,
  result: GQDeleteOrgCompanyMutation
): void;
export function updateCachedSupplierMappingRows(
  cache: Cache,
  action: CachedSupplierMappingTableRowAction.UpdateOrgCompany,
  result: GQUpsertOrgCompanyForNameMutation
): void;
export function updateCachedSupplierMappingRows<
  T extends GQDeleteOrgCompanyMutation & GQUpsertOrgCompanyForNameMutation,
>(cache: Cache, action: CachedSupplierMappingTableRowAction, result: T): void {
  const updateFn = mapActionToUpdateFn[action];

  // Update post measurement supplier mapping table rows
  cache
    .inspectFields('Query')
    .filter(
      (field) => field.fieldName === 'postMeasurementSupplierMappingTableRows'
    )
    .forEach((field) => {
      cache.updateQuery(
        {
          query: PostMeasurementSupplierMappingTableRowsDocument,
          variables: { ...field.arguments },
        },
        (data: GQPostMeasurementSupplierMappingTableRowsQuery | null) => {
          if (!data) {
            return data;
          }
          const rows =
            data.postMeasurementSupplierMappingTableRows.vendorRows.edges;
          updateFn(rows, result);
          return data;
        }
      );
    });

  // Update pre measurement supplier mapping table rows
  cache
    .inspectFields('Query')
    .filter(
      (field) => field.fieldName === 'preMeasurementSupplierMappingTableRows'
    )
    .forEach((field) => {
      cache.updateQuery(
        {
          query: PreMeasurementSupplierMappingTableRowsDocument,
          variables: { ...field.arguments },
        },
        (data: GQPreMeasurementSupplierMappingTableRowsQuery | null) => {
          if (!data) {
            return data;
          }
          const rows =
            data.preMeasurementSupplierMappingTableRows.vendorRows.edges;
          updateFn(rows, result);
          return data;
        }
      );
    });
}
